import { useState, useEffect, useMemo, useRef, useCallback, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useAtom, useAtomValue, useSetAtom, useStore } from 'jotai';
import { useQueryClient } from '@tanstack/react-query';
import {
  chatMessagesOf,
  chatQueueOf,
  chatSettingsOf,
  chatStateOf,
  indexingProgressByWorkspaceAtom,
  liveLlmCallsOf,
  paletteCapabilitiesAtom,
  refreshPaletteCapabilitiesAtom,
  useChatSession,
} from '@localagent/sdk/react';
import type { BackgroundWorkItem, ChatSession, ContextEntryRead, MentionRef, ImageAttachment, ProgressNode, ProgressTree, RagHitRef } from '@localagent/sdk';
import type { ImageAttachmentChip } from './ImageAttachmentChips';
import { invalidateRenamedSession, useSessions, useSession, useUpdateSession } from '../../hooks/useSessions';
import { useSessionTranscript, useEditSessionMessage, useDeepLinkMessageJump, useRunHistoryPaging } from '../../hooks/useSessionTranscript';
import { useAgentDefinitionsCompat as useAgentDefinitions } from '../../hooks/useAgentDefinitions';
import { useSessionEvents } from '../../hooks/useSessionEvents';
import { ChatInterface, type ChatMessage, type ChatInterfaceHandle, type FileTreeEntry, type MentionRef as UiMentionRef, type TriggerConfig, type TriggerSection } from '@localagent/ui';
import { toast } from '@localagent/ui/components/sonner';
import { Tooltip } from '@localagent/ui/components/tooltip';
import { useResizablePanel } from '@localagent/ui/hooks/use-resizable-panel';
import { ChatDebugPanel } from './ChatDebugPanel';
import { ChatLoadError } from './ChatLoadError';
import { ExecutionControlBar } from './ExecutionControlBar';
import { ReasoningProgress } from './ReasoningProgress';
import { ChatProcessModeControl } from '../agent-process/ChatProcessModeControl';
import { AgentEventTrail } from './AgentEventTrail';
import { LiveLlmCallsProvider } from './LiveLlmCallsProvider';
import { ChatImageScopeContext, newChatImageScope, releaseChatImageScope } from './chat-image-scope';
import { StreamingAnswer } from './StreamingAnswer';
import { streamingAnswerFor } from './live-llm-calls';
import { pairRunsToAssistantMessages } from './pair-runs-to-messages';
import { selectLiveRun, eventsInRun } from './live-run';
import { SessionContextSummaryChip } from './SessionContextSummaryChip';
import { QuestionsIndicator } from './QuestionsIndicator';
import { QuestionsTab } from './QuestionsTab';
import { MediaActionApprovalCard } from './MediaActionApprovalCard';
import { SubagentMediaApprovals } from './SubagentMediaApprovals';
import { WakeLabel } from './WakeLabel';
import { ShogunApprovalCard } from '../shogun/ShogunApprovalCard';
import { WorkspaceAttachCallout } from './WorkspaceAttachCallout';
import { useTicketForSession } from '../../hooks/useWorkManagement';
import { TicketCommentNotification } from '../work/TicketCommentNotification';
import { chatSurfaceEventsAtom } from '../../stores/chat/session-events';
import { chatDraftAtomFamily } from '../../stores/chat/atoms';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

// Auto-naming is once per session, ever (sessions ADR-024): module-level so
// route remounts of ChatSurface do not reset the guard and re-arm renaming.
const autoNamedSessions = new Set<string>();
const TASKS_COLUMN_WIDTH = 320;
import { sdkConfiguredAtom } from '../../stores/sdk/atoms';
import { holdsIdentityAtom } from '../../stores/auth/atoms';
import {
  chatFilesOpenAtom,
  chatDockOpenAtom,
  chatDockWidthAtom,
} from '../../stores/layout/atoms';
import { FilesToggleButton } from '../layout/ChatLayoutControls';
import { TasksColumn } from './tasks/TasksColumn';
import { ProcessSelector } from '../agent-process/ProcessSelector';
import {
  ChatAgentModelSelector,
  type ChatEffectiveModel,
} from './ChatAgentModelSelector';
import { SubagentModelToggle } from './SubagentModelToggle';
import { ContextRagSettingsToggle } from './ContextRagSettingsToggle';
import { ChatSettingsMenu } from './ChatSettingsMenu';
import { TurnStatusLine } from './TurnStatusLine';
import { mapTurnStatus } from './turn-status';
import { useExperienceMode } from '../../hooks/useExperienceMode';
import { WorkspaceStatusBar } from '../workspaces/WorkspaceStatusBar';
import { turnRefusalRow } from './turn-refusal-row';
import {
  ChatWorkspaceLayout,
  type ChatWorkspaceFileBrowserHandle,
} from '../workspaces/ChatWorkspaceLayout';
import { useFileMentions } from '../../hooks/useFileMentions';
import { useImageAttachments } from '../../hooks/useImageAttachments';
import { ImageAttachmentChips } from './ImageAttachmentChips';
import {
  MessageImagePlaceholders,
  type MessageImagePlaceholder,
} from './MessageImagePlaceholders';
import { MessageDiagrams } from './MessageDiagram';
import { mergeKnownEntries } from '../../views/chat-view.helpers';
import { ChatZeroState } from './ChatZeroState';
import { useWorkspaceProjection } from '../../hooks/useWorkspaces';
import { useShogunchop } from '../../providers/ShogunChopProvider';
import { getSDK } from '../../services/sdk-instance';
import {
  reidentifyCommandMessages,
  isPaletteListedCapability,
  type StoredThreadMessage,
  type CommandMessageView,
} from '../capability-palette/capability-palette.helpers';
import { isAPIError, isNotFound, QueuedImageStoreError, type Capability, type ViewContext } from '@localagent/sdk';
import { loadable } from 'jotai/utils';
import { CommandMessage } from '../capability-palette/CommandMessage';
import { isListCheckSettled, type ListCheck } from './listCheck';
import { cn } from '@localagent/ui/lib/utils';
import { ChatTabs } from './ChatTabs';
import { ParticipantTabView } from './ParticipantTabView';
import { buildChatTabs, QUESTIONS_TAB_ID, type ParticipantTabsControl } from './chat-tabs';
import { OpenParticipantTabContext } from './participant-tabs-context';
import { OpenRunningWorkContext, focusedActivityNodeAtom } from './running-work-focus';
import { COGNITIVE_TOOL_LABELS } from './progress-tree';
import type { CognitiveToolType } from '../../types/reasoning-types';
import { useChatParticipants } from '../../hooks/useChatParticipants';

/** Markdown for a re-identified command message rendered in the reloaded thread. */
function renderCommandMarkdown(view: CommandMessageView): string {
  const args = Object.entries(view.args)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)
    .join(' ');
  const header = `\`/${view.toolName}${args ? ' ' + args : ''}\``;
  const body = view.preview ? `\n\n${view.preview}` : '';
  return `${header}${body}`;
}

/** Opening moves for an empty chat. Deliberately generic — a persona-specific
 *  set would need a backend field none of the personas carry. */
const ZERO_STATE_PROMPTS = [
  'What can you help me with?',
  'Summarise what changed in this workspace recently',
  'Draft a plan for a task I describe',
];

/** A send parked by handleChatSend's readiness guard until the bound handle
 *  can carry it (sdk ADR-042). */
interface ParkedSend {
  /** Route session the send belongs to; null for a New Chat draft. */
  targetSessionId: string | null;
  content: string;
  mentions?: MentionRef[];
  viewContext?: ViewContext;
  attachments?: ImageAttachment[];
  chips?: ImageAttachmentChip[];
}

/** Live skill list for the `/` trigger, derived from the active ChatSession. */
const paletteCapsLoadableAtom = loadable(paletteCapabilitiesAtom);

/** The dock shows reasoning observability only — files live in their own panel
 *  (they are deliberately NOT part of the progress tracker). */
type DockTab = 'progress' | 'events';

export interface ChatSurfaceProps {
  /** The bound session, or null for a ready New Chat draft. */
  sessionId?: string;
  /** Open an existing session. The chat route navigates; a docked host rebinds. */
  onOpenSession: (sessionId: string) => void;
  /** A draft just persisted under this id. */
  onSessionCreated: (sessionId: string) => void;
  /** Leave the current session — it does not exist. */
  onSessionMissing: () => void;
  /** Namespaces this instance's test ids so a docked surface is addressable. */
  testIdPrefix?: string;
  /** What the host view has on screen, sent with every turn (app-shell ADR-014). */
  viewContext?: Omit<ViewContext, 'captured_at'>;
  /**
   * Seeds a fresh draft's agent by name instead of the account default. The
   * selector stays live — this is a default, never a lock (app-shell ADR-013).
   */
  defaultPersonaName?: string;
  /** Host chrome rendered in the chat header (e.g. a "new conversation" control). */
  headerSlot?: ReactNode;
  /** Text to place in the composer without sending it; cleared via the callback. */
  composerDraft?: string | null;
  onComposerDraftConsumed?: () => void;
  /** Reports readiness so a host can expose it on its own element for automation. */
  onChatStateChange?: (state: string) => void;
  participantTabs?: ParticipantTabsControl;
}

/**
 * Exactly one surface may be mounted: it owns the SDK ChatSession
 * (app-shell ADR-020 / sdk ADR-042). A second one is a defect, so it fails
 * loudly in development rather than silently binding the wrong conversation.
 */
let mountedSurfaces = 0;

// (sessions ADR-d28b1, sdk ADR-b0123) A refusal the user can act on, not an application error.
function isSendRefusal(err: unknown): boolean {
  return (isAPIError(err) && err.code === 'queue_full') || err instanceof QueuedImageStoreError;
}

// (sessions ADR-d28b1)
function sendFailureMessage(err: unknown): string {
  if (err instanceof QueuedImageStoreError) return err.message;
  if (isAPIError(err) && err.code === 'queue_full') {
    return 'The queue already holds 20 messages. Wait for the agent, or remove a queued message.';
  }
  return err instanceof Error ? `Failed to send message: ${err.message}` : 'Failed to send message';
}

export function ChatSurface({
  sessionId,
  onOpenSession,
  onSessionCreated,
  onSessionMissing,
  testIdPrefix = '',
  viewContext,
  defaultPersonaName,
  headerSlot,
  composerDraft,
  onComposerDraftConsumed,
  onChatStateChange,
  participantTabs,
}: ChatSurfaceProps) {
  // The snapshot rides the turn it was taken for: stamped when the user pressed
  // send, carried on a queued message, never re-derived at drain
  // (app-shell ADR-015).
  const viewContextRef = useRef(viewContext);
  viewContextRef.current = viewContext;
  const stampViewContext = useCallback(
    (): ViewContext | undefined =>
      viewContextRef.current
        ? { ...viewContextRef.current, captured_at: new Date().toISOString() }
        : undefined,
    [],
  );

  // Draft-first entry: with no `sessionId` in
  // the URL, the view IS a ready New Chat backed by a client-side draft — no
  // modal. The session persists on the first action (send / workspace-attach).
  const isDraft = !sessionId;

  // Unified right-hand dock: one resizable column whose tab bar switches between
  // Files (workspace file browser), Progress, and Events — never coexisting so the
  // file viewer and the reasoning tree stop competing for the right edge.
  const [dockTab, setDockTab] = useState<DockTab>('progress');
  // Show/hide for the file browser + Progress/Events dock live in shared
  // persisted atoms. File chrome lives on the Files tree (or this header when
  // the tree is hidden); the dock toggle sits here next to the panel it
  // controls. They're mutually exclusive: the file panel is preferred when
  // both are enabled (see `filesVisible`/`dockOpen` below), so a workspace chat
  // never renders both auxiliary panels at once.
  const chatFilesOpen = useAtomValue(chatFilesOpenAtom);
  const setChatFilesOpen = useSetAtom(chatFilesOpenAtom);
  const chatDockOpen = useAtomValue(chatDockOpenAtom);
  const setChatDockOpen = useSetAtom(chatDockOpenAtom);
  const [knownEntries, setKnownEntries] = useState<FileTreeEntry[]>([]);
  const [selectedRunId, setSelectedRunId] = useState<string | undefined>(undefined);
  const [selectedMessageId, setSelectedMessageId] = useState<string | undefined>(undefined);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);
  const [dockWidth, setDockWidth] = useAtom(chatDockWidthAtom);
  // Let the dock grow to ~60% of the viewport (capped 1000px) so the file
  // viewer can go genuinely wide — the old 800px cap starved it.
  const dockMaxWidth = Math.min(1000, Math.round(document.documentElement.clientWidth * 0.6));
  const dockResize = useResizablePanel({
    axis: 'horizontal',
    handleEdge: 'start',
    size: dockWidth,
    onCommit: setDockWidth,
    min: 280,
    max: dockMaxWidth,
  });
  const [isPoppedOut, setIsPoppedOut] = useState(false);
  const [popoutContainer, setPopoutContainer] = useState<HTMLElement | null>(null);
  const popoutWindowRef = useRef<Window | null>(null);
  useEffect(() => {
    mountedSurfaces += 1;
    if (mountedSurfaces > 1) {
      // Loud, but never fatal: a throw here would only ever fire in the dev
      // bundle — the one place it cannot protect production — and would take
      // the whole chat down over a remount artifact. The invariant's real gate
      // is `docked-chat-is-the-chat-interface.spec.ts`.
      console.error(
        `ChatSurface mounted ${mountedSurfaces} times; exactly one may exist (app-shell ADR-020)`,
      );
    }
    return () => {
      mountedSurfaces -= 1;
    };
  }, []);

  const chatInterfaceRef = useRef<ChatInterfaceHandle | null>(null);

  // A host view's starter prompt FILLS the composer; it is never auto-sent.
  useEffect(() => {
    if (composerDraft === null || composerDraft === undefined) return;
    chatInterfaceRef.current?.restoreInput(composerDraft);
    onComposerDraftConsumed?.();
  }, [composerDraft, onComposerDraftConsumed]);

  const [boundChat, setBoundChat] = useState<ChatSession | null>(null);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const holdsIdentity = useAtomValue(holdsIdentityAtom);
  const personaPickedOnRef = useRef<ChatSession | null>(null);
  const justPersistedIdRef = useRef<string | null>(null);
  const healedSessionIdRef = useRef<string | null>(null);
  useChatSession(boundChat);
  // Switch seam: the draft lane is current only for a real draft (app-shell ADR-026).
  const bindCurrent = sessionId
    ? boundChat?.sessionId === sessionId
    : !!boundChat?.isDraft;
  const chatSettings = useAtomValue(chatSettingsOf(boundChat?.sessionId));
  const sdkChatState = useAtomValue(chatStateOf(sessionId));
  const sdkMessages = useAtomValue(chatMessagesOf(sessionId));
  const liveLlmCalls = useAtomValue(liveLlmCallsOf(sessionId));

  // The account-scoped session list (`GET /v1/sessions/sessions`) returns BOTH
  // lanes — every entry carries its own `workspace_id` (string | null). It is
  // the tier-3 source that lets a cold-loaded / sidebar-opened session resolve
  // its workspace from its RECORD (not from launchedIds). Shared by cache key
  // with the sidebar's list, so this adds no extra request.
  const queryClient = useQueryClient();
  const sessionsQuery = useSessions(null);
  const sessionList = sessionsQuery.data;
  const refetchSessions = sessionsQuery.refetch;

  const listSession = useMemo(
    () => (sessionId ? sessionList?.find((s) => s.id === sessionId) : undefined),
    [sessionList, sessionId]
  );

  // A deep-linked `sessionId` that isn't in the currently-cached account-scoped
  // list is NOT proof the session is missing. The list is a react-query cache
  // that can predate the target session — e.g. a search deep-link, a link
  // opened in a second tab, or a session created out-of-band moments earlier
  // (the sidebar fetched an empty list at app-load, before the session
  // existed). Redirecting to `/chat` off that stale cache bounces the user off
  // a session that DOES exist. So before we can call a session "missing", we
  // must (re)fetch the list fresh for THIS target and confirm it's still
  // absent. `listVerifiedFor === sessionId` means "a fetch that reflects the
  // current server state has landed for this deep-link target".
  const [listCheck, setListCheck] = useState<ListCheck | undefined>(undefined);
  const [listVerifiedFor, setListVerifiedFor] = useState<string | undefined>(undefined);
  const listUpdates = queryClient.getQueryState(['sessions', null])?.dataUpdateCount ?? 0;
  useEffect(() => {
    if (isDraft || !sessionId) {
      setListCheck(undefined);
      setListVerifiedFor(undefined);
      return;
    }
    if (listSession) {
      setListCheck(undefined);
      setListVerifiedFor(sessionId);
      return;
    }
    setListVerifiedFor(undefined);
    setListCheck({
      sessionId,
      fromUpdate: queryClient.getQueryState(['sessions', null])?.dataUpdateCount ?? 0,
    });
    void refetchSessions();
  }, [isDraft, sessionId, listSession, refetchSessions, queryClient]);
  useEffect(() => {
    if (isListCheckSettled(listCheck, sessionId, listUpdates, sessionsQuery.isFetching)) {
      setListVerifiedFor(sessionId);
    }
  }, [listCheck, sessionId, listUpdates, sessionsQuery.isFetching]);

  // A live, just-persisted ChatSession whose sessionId matches the URL — the
  // draft-first hand-off (tier 1).
  const handoffSession =
    !isDraft && boundChat && boundChat.sessionId === sessionId ? boundChat : null;

  let activeWorkspaceId: string | null;
  let workspaceResolved: boolean;
  let sessionMissing = false;
  if (isDraft) {
    activeWorkspaceId = boundChat?.workspaceId ?? null;
    workspaceResolved = true;
  } else if (handoffSession) {
    activeWorkspaceId = handoffSession.workspaceId ?? null;
    workspaceResolved = true;
  } else if (sessionList !== undefined) {
    if (listSession) {
      activeWorkspaceId = listSession.workspace_id ?? null;
      workspaceResolved = true;
    } else if (listVerifiedFor === sessionId) {
      // A fresh list (fetched for this exact target) still lacks it → the
      // session genuinely does not exist for this account. Redirect is correct.
      activeWorkspaceId = null;
      workspaceResolved = true;
      sessionMissing = true;
    } else {
      // Cache is missing the session but a fresh fetch hasn't confirmed absence
      // yet (see the verification effect above) — NOT resolved, do not redirect
      // and do not connect on the wrong lane. This is the brief window that a
      // stale-cache deep-link used to fail on.
      activeWorkspaceId = null;
      workspaceResolved = false;
    }
  } else {
    activeWorkspaceId = null;
    workspaceResolved = false;
  }

  // The sessionId that actually drives queries + the realtime connection —
  // gated on resolution so we never connect on the wrong lane while the list
  // loads, and never target a session the account cannot see.
  const readySessionId = !isDraft && workspaceResolved && !sessionMissing ? sessionId : undefined;

  const imageScope = useMemo(
    () => newChatImageScope(readySessionId ?? null, activeWorkspaceId),
    [readySessionId, activeWorkspaceId],
  );
  useEffect(() => () => releaseChatImageScope(imageScope), [imageScope]);

  // The active workspace's root + transport, so the file browser can route to
  // the local daemon (local workspaces) instead of a cloud pod.
  const { projection: activeWorkspace } = useWorkspaceProjection(activeWorkspaceId);
  const { data: session, isLoading: loadingSession, error: sessionError } = useSession(
    activeWorkspaceId,
    readySessionId
  );
  const {
    data: messages,
    error: messagesError,
    loadOlder,
    loadingOlder,
    hasOlder,
    loadNewer,
    loadingNewer,
    hasNewer,
    gap: transcriptGap,
    reportVisibleMessages,
  } = useSessionTranscript(activeWorkspaceId, readySessionId, bindCurrent ? boundChat : null);
  // Approximate size (characters) of the assembled session context / message
  // window, derived from the SDK-owned messages — surfaced as a token estimate in
  // the context panel so a large workspace-steering bundle is visible.
  const sessionContextChars = useMemo(
    () =>
      (messages ?? []).reduce(
        (sum, m) =>
          sum +
          (typeof (m as { content?: unknown }).content === 'string'
            ? (m as { content: string }).content.length
            : 0),
        0
      ),
    [messages]
  );
  const { data: agentDefs } = useAgentDefinitions();
  const editMessageAndRewind = useEditSessionMessage(
    activeWorkspaceId,
    readySessionId,
    bindCurrent ? boundChat : null,
  );
  const updateSession = useUpdateSession(activeWorkspaceId, readySessionId);
  // Set when a turn's state_initialized disagrees with what the chip displayed
  // — a regression tripwire, never silently reconciled.
  const [modelMismatch, setModelMismatch] = useState<
    { expected: string; actual: string } | undefined
  >(undefined);
  const inputTextRef = useRef('');
  const [inputHasText, setInputHasText] = useState(false);
  const draftStore = useStore();
  const draftSessionIdRef = useRef(sessionId);
  draftSessionIdRef.current = sessionId;
  const handleInputChange = useCallback((_value: string, _selectionStart?: number) => {
    inputTextRef.current = _value;
    const hasText = _value.trim().length > 0;
    setInputHasText(prev => prev === hasText ? prev : hasText);
    const draftId = draftSessionIdRef.current;
    if (draftId) draftStore.set(chatDraftAtomFamily(draftId), _value);
  }, [draftStore]);

  const attachChatInterface = useCallback((handle: ChatInterfaceHandle | null) => {
    chatInterfaceRef.current = handle;
    const draftId = draftSessionIdRef.current;
    if (!handle || !draftId) return;
    const draft = draftStore.get(chatDraftAtomFamily(draftId));
    if (draft) handle.restoreInput(draft);
  }, [draftStore]);

  useEffect(() => {
    if (sessionId && justPersistedIdRef.current === sessionId) {
      if (inputTextRef.current) draftStore.set(chatDraftAtomFamily(sessionId), inputTextRef.current);
      return;
    }
    const draft = sessionId ? draftStore.get(chatDraftAtomFamily(sessionId)) : '';
    chatInterfaceRef.current?.restoreInput(draft);
    inputTextRef.current = draft;
    setInputHasText(draft.trim().length > 0);
  }, [sessionId, draftStore]);
  const { data: ticketMapping } = useTicketForSession(sessionId);

  // (sessions ADR-d28b1)
  const queue = useAtomValue(chatQueueOf(sessionId));

  // Session events — real-time WebSocket-driven state. A workspace-less session
  // passes `undefined`; the hook adopts a just-persisted ChatSession or connects
  // (workspace-less when there is no workspace).
  const sessionEvents = useSessionEvents(bindCurrent ? boundChat : null);
  // (sessions ADR-36b60)
  const [localQuestionsTab, setLocalQuestionsTab] = useState<'closed' | 'open' | 'selected'>('closed');
  const questionsTabOpen = participantTabs
    ? participantTabs.openIds.includes(QUESTIONS_TAB_ID)
    : localQuestionsTab !== 'closed';
  const questionsTabSelected = participantTabs
    ? participantTabs.selectedId === QUESTIONS_TAB_ID
    : localQuestionsTab === 'selected';
  const openQuestionsTab = useCallback(() => {
    if (participantTabs) participantTabs.onOpen(QUESTIONS_TAB_ID);
    else setLocalQuestionsTab('selected');
  }, [participantTabs]);
  const userQuestionCount = sessionEvents.questions.filter((q) => q.addresseeKind === 'user').length;

  // Progress-tree-driven activity feed (friendly trail). Structure + nesting
  // come from reasoningRuns; session event skeletons correlate tool steps for
  // lazy hydrate of heavy args/results.
  const chatSessionForEvents = sessionEvents.chatSession;
  const sessionEventSkeletons = useMemo(
    () => chatSessionForEvents?.sessionEvents ?? [],
    [chatSessionForEvents]
  );

  const liveRun = useMemo(
    () => selectLiveRun(sessionEvents.reasoningRuns),
    [sessionEvents.reasoningRuns],
  );

  const streamingAnswer = useMemo(
    () => streamingAnswerFor(liveRun, liveLlmCalls),
    [liveRun, liveLlmCalls],
  );

  const eventsByRun = useMemo(() => {
    const byRun = new Map<string, typeof sessionEventSkeletons>();
    for (const run of sessionEvents.reasoningRuns) {
      byRun.set(run.runId, eventsInRun(sessionEventSkeletons, run));
    }
    return byRun;
  }, [sessionEvents.reasoningRuns, sessionEventSkeletons]);

  const getRagHits = useCallback(
    (refs: RagHitRef[]): Promise<ContextEntryRead[]> => {
      const cs = chatSessionForEvents;
      if (!cs) return Promise.reject(new Error('No live session to read retrieved context'));
      return cs.readRagHits(refs);
    },
    [chatSessionForEvents]
  );

  // Hydrate one event's FULL data on expand (shared getEventData path — the
  // SAME code desktop uses). Rejects loudly with no live session so a
  // failure surfaces in the feed rather than a silent empty render.
  const getEventData = useCallback(
    (seq: number): Promise<Record<string, unknown>> => {
      const cs = chatSessionForEvents;
      if (!cs) return Promise.reject(new Error('No live session to hydrate event data'));
      return cs.getEventData(seq);
    },
    [chatSessionForEvents]
  );

  // Sequence numbers the trail is ACTUALLY rendering (tool start/result seqs).
  const [renderedEventSeqs, setRenderedEventSeqs] = useState<number[]>([]);
  const renderedSeqsByTrailRef = useRef<Map<string, number[]>>(new Map());

  const reportTrailRenderedWindow = useCallback((trailKey: string, seqs: number[]) => {
    const prevForKey = renderedSeqsByTrailRef.current.get(trailKey);
    if (
      prevForKey &&
      prevForKey.length === seqs.length &&
      prevForKey.every((s, i) => s === seqs[i])
    ) {
      return;
    }
    renderedSeqsByTrailRef.current.set(trailKey, seqs);
    const merged = new Set<number>();
    for (const arr of renderedSeqsByTrailRef.current.values()) {
      for (const s of arr) merged.add(s);
    }
    const next = [...merged].sort((a, b) => a - b);
    setRenderedEventSeqs((prev) => {
      if (prev.length === next.length && prev.every((s, i) => s === next[i])) return prev;
      return next;
    });
  }, []);

  // Batch pre-hydrate the RENDERED tool-event window in ONE request.
  // Best-effort OPTIMIZATION: the loud path is getEventData-on-expand; a
  // pre-fetch failure is logged.
  useEffect(() => {
    const cs = chatSessionForEvents;
    if (!cs || renderedEventSeqs.length === 0) return;
    const rendered = new Set(renderedEventSeqs);
    const seqs = sessionEventSkeletons
      .filter((e) => rendered.has(e.sequence_num) && e.handle !== undefined && !e.hydrated)
      .map((e) => e.sequence_num);
    if (seqs.length === 0) return;
    void cs.hydrateWindow(seqs).catch((err) => {
      console.warn('activity-feed event window pre-hydrate failed (will hydrate on expand)', err);
    });
  }, [chatSessionForEvents, sessionEventSkeletons, renderedEventSeqs]);

  const openContextDetails = useCallback(() => {
    setDockTab('progress');
    setChatDockOpen(true);
    // Prefer files closed so the dock is visible (mutually exclusive layout).
    setChatFilesOpen(false);
  }, [setChatDockOpen, setChatFilesOpen]);

  const refreshPalette = useSetAtom(refreshPaletteCapabilitiesAtom);
  const paletteWorkspaceId = boundChat?.workspaceId;
  useEffect(() => {
    refreshPalette();
  }, [paletteWorkspaceId, refreshPalette]);
  const prevExecutionStateRef = useRef(sessionEvents.executionState);
  useEffect(() => {
    const prev = prevExecutionStateRef.current;
    prevExecutionStateRef.current = sessionEvents.executionState;
    if (!bindCurrent) return;
    if (prev === 'running' && sessionEvents.executionState !== 'running') {
      refreshPalette();
      // The turn just wrote new context-store entries; refetch the session so
      // the context chip/panel show the updated memory footprint.
      if (readySessionId) {
        void queryClient.invalidateQueries({ queryKey: ['sessions', activeWorkspaceId, readySessionId] });
      }
    }
  }, [sessionEvents.executionState, refreshPalette, queryClient, readySessionId, activeWorkspaceId, bindCurrent]);

  // Publish the event log for views that derive from it (the search results
  // panel). Keyed on length so it republishes as events accumulate — the array
  // itself is mutated in place by the SDK.
  const setSurfaceEvents = useSetAtom(chatSurfaceEventsAtom);
  useEffect(() => {
    setSurfaceEvents({ events: [...sessionEvents.events], getEventData });
    return () => setSurfaceEvents(null);
  }, [sessionEvents.events.length, getEventData, setSurfaceEvents]); // eslint-disable-line react-hooks/exhaustive-deps

  // Shogun destructive-approval (DESKTOP arm). The daemon
  // gates a destructive platform_call server-side and reports it back as a
  // `confirmation_required` tool result; the event-processor derives it into
  // `sessionEvents.pendingPlatformApproval`. This card is DESKTOP-ONLY — gated on
  // a live daemon (`shogunchop.resource`); web uses the ShogunConfirmDialog modal.
  const shogunchop = useShogunchop();
  const [approvingKey, setApprovingKey] = useState<string | null>(null);
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const [dismissedApprovalKeys, setDismissedApprovalKeys] = useState<ReadonlySet<string>>(
    () => new Set()
  );

  const handleApprovePlatformCall = useCallback(async () => {
    const sc = shogunchop.resource;
    const req = sessionEvents.pendingPlatformApproval;
    if (!sc || !req) return;
    setApprovingKey(req.idempotencyKey);
    setApprovalError(null);
    try {
      // The ONLY value the client can supply is the server-stamped key — the
      // daemon records the approval; the agent's re-issued call then executes.
      await sc.approvePlatformCall(req.idempotencyKey);
      // Dismiss locally on success; the event-processor also clears the pending
      // approval once the re-issued call executes.
      setDismissedApprovalKeys((prev) => new Set(prev).add(req.idempotencyKey));
    } catch (err) {
      setApprovalError(err instanceof Error ? err.message : String(err));
    } finally {
      setApprovingKey(null);
    }
  }, [shogunchop.resource, sessionEvents.pendingPlatformApproval]);

  const handleDismissPlatformCall = useCallback(() => {
    const req = sessionEvents.pendingPlatformApproval;
    if (!req) return;
    setDismissedApprovalKeys((prev) => new Set(prev).add(req.idempotencyKey));
  }, [sessionEvents.pendingPlatformApproval]);

  const [mediaApprovalBusy, setMediaApprovalBusy] = useState(false);
  const [mediaApprovalError, setMediaApprovalError] = useState<string | null>(null);

  const handleMediaActionApprove = useCallback(() => {
    const req = sessionEvents.pendingMediaActionApproval;
    if (!req) return;
    setMediaApprovalBusy(true);
    setMediaApprovalError(null);
    try {
      sessionEvents.respondToMediaActionApproval(req.approvalId, true);
    } catch (err) {
      setMediaApprovalError(err instanceof Error ? err.message : String(err));
    } finally {
      setMediaApprovalBusy(false);
    }
  }, [sessionEvents]);

  const handleMediaActionDeny = useCallback(() => {
    const req = sessionEvents.pendingMediaActionApproval;
    if (!req) return;
    setMediaApprovalBusy(true);
    setMediaApprovalError(null);
    try {
      sessionEvents.respondToMediaActionApproval(req.approvalId, false);
    } catch (err) {
      setMediaApprovalError(err instanceof Error ? err.message : String(err));
    } finally {
      setMediaApprovalBusy(false);
    }
  }, [sessionEvents]);

  // File-browser mentions + search/glob `@` completions. Browse paths still
  // classify drag/drop; search+glob enrich the dropdown.
  const handleEntriesDiscovered = useCallback((entries: FileTreeEntry[]) => {
    setKnownEntries((prev) => mergeKnownEntries(prev, entries));
  }, []);
  const indexingByWorkspace = useAtomValue(indexingProgressByWorkspaceAtom);
  const indexingReady = activeWorkspaceId
    ? indexingByWorkspace[activeWorkspaceId]?.ready === true
    : false;
  // Images upload ON ATTACH, so the first attachment is what materializes a
  // draft session — there is no id to upload against until it exists.
  const ensureImageSession = useCallback(async () => {
    if (!boundChat) {
      throw new Error('chat session is not bound');
    }
    const persistedId = await boundChat.ensurePersisted();
    return { sessionId: persistedId, workspaceId: boundChat.workspaceId ?? null };
  }, [boundChat]);

  const imageAttachments = useImageAttachments({ ensureSession: ensureImageSession });
  const imageAttachmentsRef = useRef(imageAttachments);
  imageAttachmentsRef.current = imageAttachments;

  const { fileTrigger, toAttachments, handleNodeDragStart } = useFileMentions({
    knownEntries,
    workspaceId: activeWorkspaceId,
    mode: activeWorkspace?.assignment.routing_class,
    workspacePath: activeWorkspace?.binding?.folder_path,
    indexingReady,
    executeLocalTool: shogunchop.resource
      ? (toolId, params) => shogunchop.resource!.executeLocalTool(toolId, params)
      : undefined,
  });

  const capsLoadable = useAtomValue(paletteCapsLoadableAtom);
  const capsError =
    capsLoadable.state === 'hasError'
      ? capsLoadable.error instanceof Error
        ? capsLoadable.error.message
        : String(capsLoadable.error)
      : undefined;
  const skillTrigger = useMemo<TriggerConfig | undefined>(() => {
    const skills =
      capsLoadable.state === 'hasData'
        ? capsLoadable.data.filter(isPaletteListedCapability)
        : [];
    const skillByItemId = new Map<string, Capability>();
    const toItem = (c: Capability) => {
      // Inserted text is what skill_read resolves. Catalog → shogun::name;
      // shadowed workspace rows → framework:name (bare name is the context ADR-024 winner).
      const label =
        c.namespace === 'shogun'
          ? (c.qualifiedName ?? `shogun::${c.name}`)
          : c.shadowedBy
            ? (c.qualifiedName ?? `${c.framework}:${c.name}`)
            : c.name;
      const id = c.qualifiedName ?? label;
      skillByItemId.set(id, c);
      return { id, label, description: c.description ?? undefined };
    };
    const sections: TriggerSection[] = [
      { id: 'skills', label: 'Skills', items: skills.map(toItem) },
    ];
    return {
      char: '/',
      label: 'Skills',
      items: [],
      sections,
      emptyMessage:
        capsError ??
        (skills.length === 0
          ? capsLoadable.state === 'loading'
            ? 'Loading skills…'
            : activeWorkspaceId
              ? 'No skills in this workspace'
              : 'No catalog skills'
          : undefined),
      clientFilter: true,
      insertAsText: true,
      renderItem: (item) => {
        const cap = skillByItemId.get(item.id);
        return (
          <div
            className="min-w-0 w-full text-left"
            data-cap-name={cap?.name ?? item.label}
            data-cap-kind={cap?.kind ?? ''}
            data-cap-framework={cap?.framework ?? ''}
            data-namespace={cap?.namespace ?? ''}
          >
            <span className="flex items-center gap-1.5">
              <span className="font-medium">/{item.label}</span>
              {cap?.kind === 'skill' && (cap.namespace || cap.framework) && (
                <span
                  data-testid="capability-framework"
                  data-framework={cap.framework ?? ''}
                  data-namespace={cap.namespace ?? ''}
                  className="rounded bg-muted px-1 py-px text-micro font-medium uppercase tracking-wide text-muted-foreground"
                >
                  {cap.namespace === 'shogun' ? 'shogun' : cap.framework}
                </span>
              )}
            </span>
            {item.description && (
              <p className="truncate text-caption text-muted-foreground">{item.description}</p>
            )}
            {cap?.shadowedBy && (
              <span
                data-testid="capability-shadowed"
                data-shadowed-by={cap.shadowedBy}
                className="text-micro text-warning-text"
              >
                shadowed by {cap.shadowedBy} — {cap.qualifiedName}
              </span>
            )}
          </div>
        );
      },
    };
  }, [activeWorkspaceId, capsError, capsLoadable]);

  const hashTrigger = useMemo<TriggerConfig | undefined>(() => {
    const tools =
      capsLoadable.state === 'hasData'
        ? capsLoadable.data.filter((c) => c.kind === 'coded')
        : [];
    const setup = tools.filter((c) => c.name === 'workspace_init');
    const rest = tools.filter((c) => c.name !== 'workspace_init');
    const sections: TriggerSection[] = [];
    if (setup.length > 0) {
      sections.push({
        id: 'setup',
        label: 'Setup',
        items: setup.map((c) => ({ id: c.name, label: c.name, description: c.description })),
      });
    }
    if (rest.length > 0) {
      sections.push({
        id: 'tools',
        label: 'Tools',
        items: rest.map((c) => ({ id: c.name, label: c.name, description: c.description })),
      });
    }
    return {
      char: '#',
      label: 'Tools',
      items: [],
      sections,
      emptyMessage:
        capsError ??
        (tools.length === 0
          ? capsLoadable.state === 'loading'
            ? 'Loading tools…'
            : 'No tools in this session'
          : undefined),
      clientFilter: true,
      insertAsText: true,
      renderItem: (item) => (
        <div className="min-w-0 w-full text-left" data-cap-name={item.label} data-cap-kind="coded">
          <span className="font-medium">#{item.label}</span>
          {item.description && (
            <p className="truncate text-caption text-muted-foreground">{item.description}</p>
          )}
        </div>
      ),
    };
  }, [capsError, capsLoadable]);

  const namedDefaultAgent = useMemo(
    () => (defaultPersonaName ? agentDefs?.find((p) => p.name === defaultPersonaName) : undefined),
    [agentDefs, defaultPersonaName],
  );
  // A loaded agent list with no row for the host's named default is broken
  // seed/reconcile state. Surface it rather than quietly binding a different
  // agent than the view asked for (.claude/rules/no-fallbacks.md).
  const missingNamedDefault =
    defaultPersonaName !== undefined && agentDefs !== undefined && namedDefaultAgent === undefined;

  const selectedPersonaId = chatSettings?.personaId ?? (isDraft ? undefined : session?.persona_id ?? undefined);
  const zeroStatePersona = useMemo(
    () => agentDefs?.find((a) => a.id === selectedPersonaId),
    [agentDefs, selectedPersonaId]
  );

  // Bind by subscription — the SDK replaces and disconnects the live handle (sdk ADR-042).
  useEffect(() => {
    if (!sdkConfigured) return;
    const chat = getSDK().chat;
    const unsubscribe = chat.onLiveSessionChange((session) => {
      if (!session) justPersistedIdRef.current = null;
      setBoundChat(session);
    });
    setBoundChat(chat.liveSession);
    return unsubscribe;
  }, [sdkConfigured]);

  useEffect(() => {
    // Draft and connect both issue authenticated requests (sdk ADR-012).
    if (!holdsIdentity) return;
    let cancelled = false;
    const bind = async () => {
      const sdk = getSDK();
      if (!sessionId) {
        if (
          boundChat &&
          !boundChat.isDraft &&
          justPersistedIdRef.current === boundChat.sessionId
        ) {
          return;
        }
        if (defaultPersonaName !== undefined && agentDefs === undefined) return;
        const draft = await sdk.chat.openDraft(
          namedDefaultAgent ? { personaId: namedDefaultAgent.id } : {},
        );
        if (cancelled) return;
        if (
          draft === sdk.chat.liveSession &&
          namedDefaultAgent &&
          personaPickedOnRef.current !== draft &&
          draft.settings.personaId !== namedDefaultAgent.id
        ) {
          await draft.setPersona(namedDefaultAgent.id);
        }
        // Only the SDK's live handle is ever bound (sdk ADR-042).
        if (!cancelled) setBoundChat(sdk.chat.liveSession);
        return;
      }
      if (!workspaceResolved || sessionMissing) return;
      if (boundChat?.sessionId === sessionId) {
        // A healthy handle binds as-is; a stuck one (transport unwired at
        // persist time, or registration failed) heals through connect(), which
        // rebuilds it and announces the replacement via setLive (sdk ADR-042).
        // One heal per session: a rebuild that stays broken lands 'error', and
        // re-invoking connect on every error would loop the effect.
        if (boundChat.connectionState === 'connected') return;
        if (healedSessionIdRef.current === sessionId) return;
        healedSessionIdRef.current = sessionId;
      }
      await sdk.chat.connect(sessionId, activeWorkspaceId ?? undefined);
      if (!cancelled) setBoundChat(sdk.chat.liveSession);
    };
    void bind().catch((err) => {
      if (!cancelled) console.error('Failed to bind chat session', err);
    });
    return () => {
      cancelled = true;
    };
  }, [
    holdsIdentity,
    sessionId,
    workspaceResolved,
    sessionMissing,
    activeWorkspaceId,
    defaultPersonaName,
    agentDefs,
    namedDefaultAgent,
    boundChat,
  ]);

  const routeSessionIdRef = useRef(sessionId);
  routeSessionIdRef.current = sessionId;

  // (app-shell ADR-026)
  useEffect(() => {
    if (!boundChat) return;
    return boundChat.onPersisted((id) => {
      void queryClient.invalidateQueries({ queryKey: ['sessions', null], exact: true });
      if (routeSessionIdRef.current !== undefined) return;
      justPersistedIdRef.current = id;
      onSessionCreated(id);
    });
  }, [boundChat, onSessionCreated, queryClient]);

  useEffect(() => {
    if (sessionId && justPersistedIdRef.current === sessionId) {
      justPersistedIdRef.current = null;
    }
  }, [sessionId]);

  // A dropped handle ends any pending persist handoff (app-shell ADR-026).
  useEffect(() => {
    if (!boundChat) {
      justPersistedIdRef.current = null;
    }
  }, [boundChat]);

  const personaSettling =
    isDraft &&
    defaultPersonaName !== undefined &&
    namedDefaultAgent !== undefined &&
    personaPickedOnRef.current !== boundChat &&
    selectedPersonaId !== namedDefaultAgent.id;

  // The agent whose config feeds the draft chip (persisted sessions read the
  // server-resolved effective_agent_config instead — see effectiveModel below).
  const selectedAgent = useMemo(
    () => agentDefs?.find((p) => p.id === selectedPersonaId),
    [agentDefs, selectedPersonaId]
  );
  const { mode } = useExperienceMode();
  const showAdvancedChrome = mode === 'advanced';
  const showNormalStatus = mode === 'normal';

  const automaticProcessSelection = chatSettings?.automaticProcessSelection ?? false;
  const automaticProcessSelectionInherited = chatSettings?.automaticInherited ?? true;
  const processId = chatSettings?.processId ?? session?.agent_process_id;
  const internalAgentVisibility = chatSettings?.internalAgentVisibility ?? 'friendly';

  const effectiveModel: ChatEffectiveModel | undefined = chatSettings?.effectiveModel;

  // Passive LM Studio warning: effective provider is lmstudio and the
  // persisted session has no bound device. Display-only — send still fails loud.
  const deviceUnbound =
    !isDraft &&
    !!effectiveModel &&
    !('error' in effectiveModel) &&
    effectiveModel.providerType === 'lmstudio' &&
    !(session as { device_id?: string | null } | undefined)?.device_id;

  // The mismatch tripwire (below) is a regression guard: it fires only when a
  // turn the user INITIATES IN THIS VIEW reports a provider/model different from
  // what the chip displayed when they sent it. It must NOT fire for historical
  // turns whose model legitimately differs from the current chip — the user may
  // have switched the agent, or edited the persona's model between that turn and
  // now. `mismatchIgnoreSeqRef` is the highest event sequence_num to ignore:
  //   • session load → MAX (ignore ALL pre-existing turns; nothing initiated yet)
  //   • agent switch → live max (ignore the prior turn; the switch is deliberate)
  //   • message send → live max (so ONLY the upcoming turn's state_initialized,
  //     with a higher sequence, is compared against the chip that was showing).
  //
  // NOTE: `sessionEvents.events` is a STABLE array instance mutated in place by
  // the ChatSession (see useSessionEvents) — its reference never changes, so the
  // mismatch effect keys off `events.length`, and every "latest sequence"
  // snapshot READS the live array at the moment it's needed (an effect keyed on
  // the array reference would never re-run).
  const mismatchIgnoreSeqRef = useRef(Number.MAX_SAFE_INTEGER);
  const liveMaxEventSeq = useCallback(
    () => sessionEvents.events.reduce((max, e) => Math.max(max, e.sequence_num), -1),
    [sessionEvents.events]
  );
  useEffect(() => {
    mismatchIgnoreSeqRef.current = Number.MAX_SAFE_INTEGER;
    setModelMismatch(undefined);
  }, [sessionId]);

  // Change the agent: patch a persisted session, or update the draft so the
  // choice is applied on persist. The
  // selector emits the persona ID directly (no name→id lookup).
  const handleSelectAgent = useCallback(
    (personaId: string) => {
      if (!boundChat) {
        throw new Error('chat session is not bound');
      }
      setModelMismatch(undefined);
      mismatchIgnoreSeqRef.current = liveMaxEventSeq();
      personaPickedOnRef.current = boundChat;
      void boundChat.setPersona(personaId).catch((err: unknown) => {
        toast.error(
          err instanceof Error ? `Failed to change agent: ${err.message}` : 'Failed to change agent',
        );
      });
    },
    [boundChat, liveMaxEventSeq]
  );

  // Chip mismatch tripwire: when the LATEST turn's state_initialized
  // (one started under the current selection) reports a provider/model different
  // from what the chip displayed, surface it loudly — never silently reconcile.
  // Keyed on events.length (the hook's recompute signal for the in-place array).
  // Reads event.data.agent_config.{provider_type,model_name}.
  useEffect(() => {
    if (!effectiveModel || 'error' in effectiveModel) return;
    const candidates = sessionEvents.events
      .filter(
        (e) => e.event_type === 'state_initialized' && e.sequence_num > mismatchIgnoreSeqRef.current
      )
      .sort((a, b) => a.sequence_num - b.sequence_num);
    const si = candidates[candidates.length - 1];
    if (!si) return;
    const cfg = (si.data as { agent_config?: { provider_type?: string; model_name?: string } })
      ?.agent_config;
    if (!cfg?.provider_type || !cfg?.model_name) return;
    const expected = `${effectiveModel.providerType}/${effectiveModel.modelName}`;
    const actual = `${cfg.provider_type}/${cfg.model_name}`;
    setModelMismatch(expected === actual ? undefined : { expected, actual });
  }, [sessionEvents.events, sessionEvents.events.length, effectiveModel]);

  // Draft-first: landing on `/chat` with no
  // sessionId shows a ready New Chat — no modal, no auto-redirect to the most
  // recent session. Past sessions are reachable from the sidebar. Nothing is
  // persisted until the first action.

  // If we're on /chat/{sessionId} but the session doesn't exist, redirect to
  // /chat. "Missing" is decided by a FRESH account-scoped list fetched for this
  // exact target (`sessionMissing`, gated on `listVerifiedFor`) or by the record
  // fetch resolving empty — never by a premature guess off a stale cache or
  // while the list is still loading.
  useEffect(() => {
    if (isDraft) return;
    if (sessionMissing) {
      onSessionMissing();
      return;
    }
    if (readySessionId && !loadingSession && !session && !(sessionError && !isNotFound(sessionError))) {
      onSessionMissing();
    }
  }, [isDraft, sessionMissing, readySessionId, loadingSession, session, sessionError, onSessionMissing]);


  const autoGenerateSessionName = useCallback(
    async (
      targetSessionId: string,
      workspaceId: string | null,
      userMessage: string,
      assistantMessage: string
    ) => {
      try {
        const result = await getSDK().reasoning.generateSessionName(workspaceId, {
          user_message: userMessage,
          assistant_message: assistantMessage,
        });
        if (!result.name) return;
        // Re-check after the await: the user or a concurrent turn may have
        // titled the session while generation was in flight.
        const current = await getSDK().sessions.get(workspaceId, targetSessionId);
        if (current.name && !current.is_default_name) return;
        // Bind the PATCH to the session captured at fire time — the route (and
        // with it the useUpdateSession closure, which resolves mutationFn from
        // the LATEST render) may have moved on during the await. Going through
        // updateSession.mutate here retargeted the rename to the newly opened
        // session; the explicit id cannot.
        await getSDK().sessions.update(workspaceId, targetSessionId, {
          name: result.name,
          name_only_if_default: true,
        });
        invalidateRenamedSession(queryClient, workspaceId);
      } catch (error) {
        console.error('Failed to auto-generate session name:', error);
      }
    },
    [queryClient]
  );

  // Auto-generate session name when we see the first assistant_message event
  useEffect(() => {
    if (!sessionId) return;
    if (autoNamedSessions.has(sessionId)) return;
    // Never overwrite a real title — only replace the default marker
    // (server-authoritative flag, sessions ADR-024).
    if (session?.name && !session.is_default_name) return;
    // Events-ownership guard: sessionEvents trails the bound ChatSession, which
    // lags the route across navigation — the stale window still holds the
    // PREVIOUS session's events. Only auto-name once they belong to this one.
    if (boundChat?.sessionId !== sessionId) return;
    // Look for the first assistant_message in this session's events
    // Skeleton events carry no `content` (sdk ADR-89da5); the text comes from the transcript.
    const replied = sessionEvents.events.some((e) => e.event_type === 'assistant_message');
    if (!replied) return;
    const transcript = (messages ?? []) as unknown as StoredThreadMessage[];
    const firstText = (role: 'user' | 'assistant') =>
      transcript.find((m) => m.role === role && typeof m.content === 'string' && m.content.trim() !== '')
        ?.content as string | undefined;
    const userContent = firstText('user');
    const assistantContent = firstText('assistant');
    if (!userContent || !assistantContent) return;
    autoNamedSessions.add(sessionId);
    void autoGenerateSessionName(sessionId, activeWorkspaceId, userContent, assistantContent);
  }, [sessionEvents.events, messages, activeWorkspaceId, sessionId, session, boundChat, autoGenerateSessionName]);

  // Turn-end tree refresh: when the
  // chat leaves a running turn (reasoning → idle/stopped/error all collapse to
  // executionState 'running' → 'idle'), refresh the workspace file tree so
  // agent-written files appear without user action. Automatic (non-manual):
  // never wakes a hibernated pod. The handle is null when no workspace is
  // attached or the file panel is closed — then there is no tree to refresh.
  const fileBrowserRef = useRef<ChatWorkspaceFileBrowserHandle | null>(null);
  const prevExecutionStateForFiles = useRef(sessionEvents.executionState);
  useEffect(() => {
    const prev = prevExecutionStateForFiles.current;
    prevExecutionStateForFiles.current = sessionEvents.executionState;
    if (!bindCurrent) return;
    if (prev === 'running' && sessionEvents.executionState === 'idle') {
      void fileBrowserRef.current?.refreshTree();
    }
  }, [sessionEvents.executionState, bindCurrent]);

  // Sends parked by the readiness guard in handleChatSend, flushed in order
  // once the bound handle can carry them (sdk ADR-042).
  const pendingSendsRef = useRef<ParkedSend[]>([]);

  const sendViaHandle = useCallback(
    (
      handle: ChatSession,
      content: string,
      mentions?: MentionRef[],
      viewContext?: ViewContext,
      attachments?: ImageAttachment[],
      chips: ImageAttachmentChip[] = [],
    ) => {
      void handle.sendAsync(content, mentions, viewContext, attachments).then(
        () => imageAttachmentsRef.current.releaseAttachments(chips),
        (err: unknown) => {
          if (isSendRefusal(err)) {
            console.warn('Send refused:', err);
          } else {
            console.error('Failed to send message:', err);
          }
          toast.error(sendFailureMessage(err));
          chatInterfaceRef.current?.restoreInput(content);
          imageAttachmentsRef.current.restoreAttachments(chips);
        },
      );
    },
    [],
  );

  const parkSend = (
    content: string,
    mentions?: MentionRef[],
    attachments?: ImageAttachment[],
    chips?: ImageAttachmentChip[],
  ) => {
    const viewContext = stampViewContext();
    pendingSendsRef.current = [
      ...pendingSendsRef.current,
      {
        targetSessionId: sessionId ?? null,
        content,
        ...(mentions ? { mentions } : {}),
        ...(viewContext ? { viewContext } : {}),
        ...(attachments ? { attachments } : {}),
        ...(chips && chips.length > 0 ? { chips } : {}),
      },
    ];
    toast('Message will send once the chat session connects');
  };

  // Flush parked sends. connectionState is a dep on purpose: a heal that keeps
  // the same handle object reaches 'connected' without a new boundChat identity.
  useEffect(() => {
    const parked = pendingSendsRef.current;
    const routeTarget = sessionId ?? null;
    const ready = parked.filter((p) => p.targetSessionId === routeTarget);
    if (ready.length === 0 || !boundChat) return;
    if (sessionId !== undefined) {
      // Route session: flush only into the route's connected handle. A terminal
      // connection failure puts the message back in the composer instead of
      // dying as a raw connection toast (no-fallbacks).
      if (boundChat.sessionId !== sessionId || boundChat.isDraft) return;
      if (boundChat.connectionState === 'error') {
        pendingSendsRef.current = parked.filter((p) => p.targetSessionId !== routeTarget);
        chatInterfaceRef.current?.restoreInput(ready[0].content);
        imageAttachmentsRef.current.restoreAttachments(ready.flatMap((p) => p.chips ?? []));
        toast.error(
          'Chat session connection failed — your message is back in the input. Reopen the chat to try again.',
        );
        return;
      }
      if (boundChat.connectionState !== 'connected') return;
    } else if (!boundChat.isDraft) {
      // New Chat: flush once the draft handle is bound — sending on it is the
      // persist trigger. A leftover persisted handle is not the target.
      return;
    }
    pendingSendsRef.current = parked.filter((p) => p.targetSessionId !== routeTarget);
    for (const p of ready) {
      sendViaHandle(boundChat, p.content, p.mentions, p.viewContext, p.attachments, p.chips);
    }
  }, [boundChat, sessionId, sessionEvents.connectionState, sendViaHandle]);

  const handleChatSend = (content: string, uiMentions?: UiMentionRef[]) => {
    const mentions = uiMentions && uiMentions.length > 0 ? toAttachments(uiMentions) : undefined;
    // Images are already uploaded (chips gate send until they are), so the send
    // carries handles. Chips clear here, alongside the composer.
    const images =
      imageAttachments.readyAttachments.length > 0
        ? imageAttachments.readyAttachments
        : undefined;
    const chips = images ? imageAttachments.detachAttachments() : [];
    // Arm the mismatch tripwire for THIS turn: only a state_initialized with a
    // sequence beyond the current max (i.e. this turn's) is compared against the
    // chip the user is sending under. Historical turns stay ignored.
    mismatchIgnoreSeqRef.current = liveMaxEventSeq();

    if (!boundChat) {
      parkSend(content, mentions, images, chips);
      return;
    }

    // Send-readiness guard (sdk ADR-042): the composer and the zero-state
    // prompts both route here, and a click can land before the bind effect has
    // produced a live handle — the route names a session the bound handle does
    // not match (bind still in flight; sending on a leftover draft would fork a
    // second session), or the persisted handle's transport is not up yet. Park
    // until bound+connected instead of failing with a raw connection-error
    // toast; the flush effect surfaces a terminal connection failure.
    const target = sessionId ?? null;
    const routeMatched = (boundChat.sessionId ?? null) === target;
    const liveReady = boundChat.isDraft || sessionEvents.connectionState === 'connected';
    if (!routeMatched || !liveReady) {
      parkSend(content, mentions, images, chips);
      return;
    }

    sendViaHandle(boundChat, content, mentions, stampViewContext(), images, chips);
  };

  const requireBound = (): ChatSession => {
    if (!boundChat) throw new Error('No chat session is bound');
    return boundChat;
  };

  const handleRedirect = () => {
    const text = inputTextRef.current.trim();
    if (!text || sessionEvents.executionState !== 'running') return;
    sessionEvents.redirectExecution(text);
    inputTextRef.current = '';
    setInputHasText(false);
    chatInterfaceRef.current?.restoreInput('');
  };

  const handleEditMessage = (messageId: string, newContent: string) => {
    if (!session || !sessionId) return;

    void (async () => {
      // Rewind on the backend: deletes the edited message and everything
      // after it. The re-send below appends the edited content as a fresh
      // user message and re-executes the agent from the rewound state.
      await editMessageAndRewind(messageId, newContent);

      // Re-send via WS — non-blocking
      sessionEvents.sendMessage(newContent, undefined, stampViewContext());
    })();
  };

  const handleRetryMessage = (messageId: string, content: string) => {
    handleEditMessage(messageId, content);
  };

  const handleSaveSessionName = () => {
    const trimmed = editNameValue.trim();
    if (trimmed && trimmed !== session?.name) {
      updateSession.mutate({ name: trimmed });
    }
    setIsEditingName(false);
  };


  // Message↔run pairing for highlight sync. Built from REST message ids +
  // completed runs (event-id assistantMessageId alone does not match REST).
  const runByMessageId = useMemo(() => {
    const raw = (messages ?? []) as unknown as StoredThreadMessage[];
    const assistantMsgs: Array<{ id: string; isCommand: boolean }> = [];
    for (const item of reidentifyCommandMessages(raw)) {
      // Command rows are kind:'command' — excluded so they don't consume a run slot.
      if (item.kind === 'message' && item.message.role === 'assistant') {
        assistantMsgs.push({ id: item.message.id, isCommand: false });
      }
    }
    return pairRunsToAssistantMessages(sessionEvents.reasoningRuns, assistantMsgs);
  }, [messages, sessionEvents.reasoningRuns]);

  const messageToRunId = useMemo(() => {
    const map = new Map<string, string>();
    for (const [msgId, run] of runByMessageId) {
      map.set(msgId, run.runId);
    }
    return map;
  }, [runByMessageId]);

  const runIdToMessageId = useMemo(() => {
    const map = new Map<string, string>();
    for (const [msgId, run] of runByMessageId) {
      map.set(run.runId, msgId);
    }
    return map;
  }, [runByMessageId]);

  const {
    loadOlderRuns,
    loadingOlderRuns,
    olderRunsError,
    hasOlderRuns,
    loadNewerRuns,
    loadingNewerRuns,
    newerRunsError,
    hasNewerRuns,
    runsGap,
    reportVisibleRuns,
  } = useRunHistoryPaging(
    bindCurrent ? boundChat : null,
  );

  const [scrollTargetMessageId, setScrollTargetMessageId] = useState<string | undefined>(undefined);
  const selectRunRequestRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!scrollTargetMessageId) return;
    const selector = `[data-message-id="${scrollTargetMessageId}"]`;
    const scrollTo = (): boolean => {
      const el = document.querySelector(selector);
      if (!el) return false;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setScrollTargetMessageId(undefined);
      return true;
    };
    if (scrollTo()) return;
    const observer = new MutationObserver(() => {
      if (scrollTo()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    const giveUp = setTimeout(() => {
      observer.disconnect();
      setScrollTargetMessageId(undefined);
    }, 5000);
    return () => {
      observer.disconnect();
      clearTimeout(giveUp);
    };
  }, [scrollTargetMessageId]);

  // Handle run selection → highlight the linked chat message and scroll to it
  const handleSelectRun = useCallback(
    async (runId: string) => {
      // Toggle off if clicking the same run
      if (selectedRunId === runId) {
        selectRunRequestRef.current = undefined;
        setSelectedRunId(undefined);
        setSelectedMessageId(undefined);
        return;
      }
      selectRunRequestRef.current = runId;
      setSelectedRunId(runId);
      let msgId = runIdToMessageId.get(runId);
      const run = sessionEvents.reasoningRuns.find((r) => r.runId === runId);
      if (!msgId && run?.assistantMessageId && bindCurrent && boundChat) {
        let revealed: boolean;
        try {
          revealed = await boundChat.revealMessage(run.assistantMessageId);
        } catch (err) {
          console.error("Failed to load the run's reply:", err);
          toast.error(
            err instanceof Error ? `Failed to load the run's reply: ${err.message}` : "Failed to load the run's reply",
          );
          return;
        }
        if (selectRunRequestRef.current !== runId) return;
        if (revealed) msgId = run.assistantMessageId;
      }
      setSelectedMessageId(msgId);
      setScrollTargetMessageId(msgId);
    },
    [selectedRunId, runIdToMessageId, sessionEvents.reasoningRuns, bindCurrent, boundChat]
  );

  // Handle clicking an assistant message → highlight the corresponding run
  const handleMessageClick = useCallback(
    (messageId: string) => {
      selectRunRequestRef.current = undefined;
      if (selectedMessageId === messageId) {
        setSelectedRunId(undefined);
        setSelectedMessageId(undefined);
        return;
      }
      setSelectedMessageId(messageId);
      setSelectedRunId(messageToRunId.get(messageId));
    },
    [messageToRunId, selectedMessageId]
  );

  // Deep-link to a specific turn: account-search session results navigate to
  // `/chat/:session_id#event=<session_context_entry_id>`. The jump policy
  // (cap + manual continuation) is the hook; this surface binds it to the
  // shared highlight state.
  const location = useLocation();
  const {
    jumpNeedsMore: deepLinkNeedsMore,
    armManualJump: armDeepLinkLoad,
  } = useDeepLinkMessageJump({
    hash: location.hash,
    messages,
    hasOlder,
    loadOlder,
    onTarget: setSelectedMessageId,
  });

  // Cleanup popout on unmount
  useEffect(() => {
    return () => {
      popoutWindowRef.current?.close();
    };
  }, []);

  const handlePopOut = () => {
    const win = window.open('', 'debug-panel', 'width=420,height=700');
    if (!win) return;

    popoutWindowRef.current = win;
    win.document.title = 'Side Panel';

    // Copy stylesheets for consistent rendering
    document.querySelectorAll('link[rel="stylesheet"], style').forEach((el) => {
      win.document.head.appendChild(el.cloneNode(true));
    });
    win.document.documentElement.className = document.documentElement.className;

    const container = win.document.createElement('div');
    win.document.body.style.margin = '0';
    win.document.body.appendChild(container);
    setPopoutContainer(container);
    setIsPoppedOut(true);

    win.addEventListener('beforeunload', () => {
      popoutWindowRef.current = null;
      setPopoutContainer(null);
      setIsPoppedOut(false);
    });
  };

  const handlePopIn = () => {
    popoutWindowRef.current?.close();
    popoutWindowRef.current = null;
    setPopoutContainer(null);
    setIsPoppedOut(false);
  };

  // The dock shows Progress/Events (files live in their own panel). Both need a
  // live session; fall back to the first available if the remembered tab isn't.
  const dockTabs: DockTab[] = session ? ['progress', 'events'] : [];
  const effectiveDockTab: DockTab | null = dockTabs.includes(dockTab)
    ? dockTab
    : (dockTabs[0] ?? null);

  // Mutual exclusion (four-column squish fix): the file panel and the dock are
  // never both shown. The file panel shows only for a workspace-attached chat;
  // the dock yields to it when both are enabled (files preferred, IDE-like).
  const filesVisible = !!activeWorkspaceId && chatFilesOpen;
  const dockOpen = showAdvancedChrome && chatDockOpen && effectiveDockTab !== null && !filesVisible;

  const toggleDock = () => {
    if (dockOpen) {
      setChatDockOpen(false);
    } else {
      setChatDockOpen(true);
      setChatFilesOpen(false);
    }
  };

  // The chat header (session name + controls) as a named node. Hoisted out of
  // ChatInterface's headerSlot so the workspace layout can render it FULL-WIDTH
  // above the directory tree — the tree sits UNDER the header bar, not beside
  // it. The title stays on the left; the controls group right so they never
  // crowd the title. A workspace-less chat renders this same node inline as
  // ChatInterface's headerSlot (see renderContent's `includeHeader`).
  const chatHeader = (
    <div className="space-y-1">
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-2">
          {isEditingName ? (
            <input
              ref={nameInputRef}
              value={editNameValue}
              onChange={(e) => setEditNameValue(e.target.value)}
              onBlur={handleSaveSessionName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSaveSessionName();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setIsEditingName(false);
                }
              }}
              data-testid="session-name-input"
              className="rounded-md bg-muted px-1 text-body font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              autoFocus
            />
          ) : (
            <h1
              className={cn(
                'cursor-pointer truncate text-body font-semibold hover:text-primary/80',
                !session?.name && 'font-normal text-muted-foreground',
              )}
              onClick={() => {
                setEditNameValue(session?.name || '');
                setIsEditingName(true);
              }}
              title="Click to edit session name"
              data-testid="session-title"
            >
              {session?.name || 'Untitled chat'}
            </h1>
          )}
        </div>
        <div className="flex min-w-0 items-center gap-2 overflow-x-auto">
          {headerSlot}
          <ChatAgentModelSelector
            agents={(agentDefs ?? []).map((p) => ({
              id: p.id,
              name: p.name,
              emoji: p.emoji ?? undefined,
            }))}
            selectedPersonaId={selectedPersonaId}
            onSelect={handleSelectAgent}
            effectiveModel={effectiveModel}
            mismatch={modelMismatch}
            deviceUnbound={deviceUnbound}
            loading={agentDefs === undefined || (!isDraft && loadingSession)}
          />
          {showAdvancedChrome ? (
            <ChatSettingsMenu
              processControl={
                <ChatProcessModeControl
                  automatic={automaticProcessSelection}
                  inherited={automaticProcessSelectionInherited}
                  automaticCandidateCount={selectedAgent?.agent_config?.automatic_process_ids?.length}
                  personaName={selectedAgent?.name ?? 'this agent'}
                  internalAgentVisibility={internalAgentVisibility}
                  onAutomaticChange={async (enabled) => {
                    await boundChat?.setAutomaticProcessSelection(enabled);
                  }}
                  onInherit={async () => {
                    await boundChat?.setAutomaticProcessSelection(null);
                  }}
                  onInternalAgentVisibilityChange={async (visibility) => {
                    await boundChat?.setInternalAgentVisibility(visibility);
                  }}
                  manualProcessControl={
                    <ProcessSelector
                      agentProcessId={processId}
                      agentProcessName={isDraft ? undefined : session?.agent_process_name}
                      onSelect={(nextProcessId) => {
                        void boundChat?.setProcess(nextProcessId);
                      }}
                    />
                  }
                />
              }
              contextRagControl={
                <ContextRagSettingsToggle
                  rootChecked={chatSettings?.contextRagEnabled ?? true}
                  childChecked={chatSettings?.contextRagChildRunsEnabled ?? false}
                  disabled={!isDraft && loadingSession}
                  onRootChange={(checked) => {
                    void boundChat?.setContextRagEnabled(checked);
                  }}
                  onChildChange={(checked) => {
                    void boundChat?.setContextRagChildRunsEnabled(checked);
                  }}
                />
              }
              modelPinControl={
                <SubagentModelToggle
                  checked={chatSettings?.pinModelToTurn ?? false}
                  modelName={
                    effectiveModel && 'modelName' in effectiveModel
                      ? effectiveModel.modelName
                      : undefined
                  }
                  disabled={!isDraft && loadingSession}
                  onChange={(checked) => {
                    void boundChat?.setPinModelToTurn(checked);
                  }}
                />
              }
            />
          ) : null}
          <FilesToggleButton hasWorkspace={!!activeWorkspaceId} open={filesVisible} />
          {showAdvancedChrome ? (
            <Tooltip label={dockOpen ? 'Hide progress panel' : 'Show progress panel'}>
              <button
                type="button"
                data-testid="toggle-dock"
                onClick={toggleDock}
                className={`rounded-md p-1.5 transition-colors ${dockOpen ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'}`}
                title={dockOpen ? 'Hide progress panel' : 'Show progress panel'}
                aria-label={dockOpen ? 'Hide progress panel' : 'Show progress panel'}
                aria-pressed={dockOpen}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 12h4l3 7 4-14 3 7h4" />
                </svg>
              </button>
            </Tooltip>
          ) : null}
        </div>
      </div>
      {activeWorkspaceId && (
        <WorkspaceStatusBar
          workspaceId={activeWorkspaceId}
          onDetach={isDraft ? () => boundChat?.clearWorkspace() : undefined}
        />
      )}
    </div>
  );

  const renderRunStatus = (message: ChatMessage, run: ProgressTree | undefined) => {
    if (showNormalStatus) {
      const rows = mapTurnStatus(run, {
        runPersonaName: selectedAgent?.name ?? 'Assistant',
      });
      if (rows.length === 0 && !message.thinking_content) return null;
      return (
        <TurnStatusLine
          key={`status-${run ? run.runId : message.id}`}
          rows={rows}
          thinkingContent={message.thinking_content ?? undefined}
        />
      );
    }
    if (!showAdvancedChrome) return null;
    // A turn can have reasoning and no run; returning null there would drop
    // the reasoning entirely now that it lives in the trail.
    if (!run && !message.thinking_content) return null;
    return (
      <AgentEventTrail
        key={`trail-${run ? run.runId : message.id}`}
        progressTree={run ?? null}
        internalAgentVisibility={internalAgentVisibility}
        thinkingContent={message.thinking_content ?? undefined}
        events={run ? (eventsByRun.get(run.runId) ?? []) : sessionEventSkeletons}
        getEventData={getEventData}
        isReasoning={false}
        onRenderedWindowChange={(seqs) =>
          reportTrailRenderedWindow(`completed-${run ? run.runId : message.id}`, seqs)
        }
      />
    );
  };

  const renderContent = (includeHeader = true) => {
    // Switch seam (app-shell ADR-026), refined for the windowed transcript:
    // a trailing handle still
    // paints the URL-keyed CACHED transcript while the connect runs — the
    // persisted read is this session's, so there is no wrong-session paint —
    // but only when a cached page set exists; a session the client has never
    // read renders the loading state until bound. The live/derived rows below
    // stay bind-gated.
    if (!bindCurrent && !(messages && messages.length > 0)) {
      return (
        <LoadingState label="Loading conversation..." className="h-full" />
      );
    }

    // Re-identify persisted user-invoked capability pairs (assistant tool_call
    // entry + matching tool result in the 'session' lane) as command messages,
    // so a reloaded thread renders them as commands — never as an empty
    // assistant bubble + raw tool row. The getMessages response
    // carries tool_calls / tool-role rows verbatim (SessionHistoryMessage);
    // the SDK Message type just doesn't declare them.
    if (messagesError) {
      return <ChatLoadError error={messagesError} />;
    }
    const rawMessages = (messages ?? []) as unknown as StoredThreadMessage[];
    const chatMessages: ChatMessage[] = [];
    // Image attachment rows precede the user message they belong to, so they
    // accumulate here and ride onto that message's bubble as image thumbnails
    // instead of rendering as stub bubbles of their own.
    let pendingImagePlaceholders: MessageImagePlaceholder[] = [];
    for (const item of reidentifyCommandMessages(rawMessages)) {
      if (item.kind === 'command') {
        chatMessages.push({
          id: item.view.id,
          role: 'assistant',
          // `content` is the plain-markdown fallback (copy/accessibility, and the
          // render path when renderMessage is not wired); the structured view is
          // threaded through metadata so the thread renders the same
          // CommandMessage component the palette/pending state uses.
          content: renderCommandMarkdown(item.view),
          timestamp: '',
          metadata: { __command_message: true, __command_view: item.view },
        });
        continue;
      }
      const m = item.message;
      if (
        m.entry_type === 'attachment' &&
        typeof m.attachment_path === 'string' &&
        m.attachment_path.startsWith('image://')
      ) {
        pendingImagePlaceholders.push({ handle: m.attachment_path });
        continue;
      }
      if (m.role !== 'user' && m.role !== 'assistant') continue;
      const imagePlaceholders = m.role === 'user' && pendingImagePlaceholders.length > 0
        ? pendingImagePlaceholders
        : undefined;
      if (imagePlaceholders) pendingImagePlaceholders = [];
      chatMessages.push({
        id: m.id,
        role: m.role,
        content: m.content,
        timestamp: m.created_at ? new Date(m.created_at).toLocaleTimeString() : '',
        metadata: imagePlaceholders
          ? { ...(m.metadata ?? {}), __image_placeholders: imagePlaceholders }
          : (m.metadata as Record<string, unknown> | undefined),
        thinking_content: m.thinking_content,
      });
    }

    // The derived live rows are the BOUND handle's state — while the switch
    // seam paints the cached transcript unbound, they must not cross (app-shell ADR-026).
    const liveRows = bindCurrent ? sdkMessages : [];
    for (const row of liveRows) {
      if (!row.id.startsWith('pending-')) continue;
      if (chatMessages.some((m) => m.role === 'user' && m.content === row.content)) continue;
      chatMessages.push({
        id: row.id,
        role: 'user',
        content: row.content,
        timestamp: row.timestamp ? new Date(row.timestamp).toLocaleTimeString() : '',
      });
    }

    const refusalRow = turnRefusalRow(sessionEvents);
    if (refusalRow) {
      chatMessages.push({
        ...refusalRow,
        role: 'assistant',
        timestamp: new Date().toLocaleTimeString(),
      });
    }

    return (
      <>
      {deepLinkNeedsMore && (
        <div
          className="flex items-center justify-center gap-3 py-2"
          data-testid="chat-deep-link-load-more"
        >
          <span className="text-caption text-muted-foreground">
            The linked message is further back in this conversation.
          </span>
          <button
            type="button"
            onClick={armDeepLinkLoad}
            className="rounded-md border border-border px-2.5 py-1 text-caption font-medium hover:bg-secondary"
          >
            Load more
          </button>
        </div>
      )}
      <LiveLlmCallsProvider calls={liveLlmCalls}>
      <ChatImageScopeContext.Provider value={imageScope}>
      <ChatInterface
        ref={attachChatInterface}
        testIdPrefix={testIdPrefix}
        messages={chatMessages}
        onLoadOlder={hasOlder ? loadOlder : undefined}
        loadingOlder={loadingOlder}
        hasOlder={hasOlder}
        onLoadNewer={hasNewer ? loadNewer : undefined}
        loadingNewer={loadingNewer}
        hasNewer={hasNewer}
        gap={transcriptGap}
        onVisibleMessagesChange={reportVisibleMessages}
        renderBeforeMessage={(message) => {
          if (message.role !== 'assistant') return null;
          if (message.metadata?.__command_message) return null;
          const run = runByMessageId.get(message.id);
          const status = renderRunStatus(message, run);
          if (!run || (run.trigger !== 'background' && run.trigger !== 'agent_message')) return status;
          return (
            <>
              <WakeLabel run={run} testId="message-wake-label" className="mb-1 inline-block" />
              {status}
            </>
          );
        }}
        renderMessage={(message) =>
          message.metadata?.__command_message ? (
            <CommandMessage view={message.metadata.__command_view as CommandMessageView} />
          ) : undefined
        }
        emptyState={
          <ChatZeroState
            personaName={zeroStatePersona?.name}
            personaDescription={zeroStatePersona?.description ?? undefined}
            starterPrompts={ZERO_STATE_PROMPTS}
            recentSessions={(sessionList ?? [])
              .slice(0, 3)
              .map((s) => ({ id: s.id, name: s.name }))}
            onSendMessage={(text) => handleChatSend(text)}
            onOpenSession={onOpenSession}
          />
        }
        onSendMessage={handleChatSend}
        onAttachImages={imageAttachments.attachFiles}
        onRejectAttachments={(names) =>
          toast.error(
            `Only PNG, JPEG, single-frame GIF and WebP images can be attached — refused ${names.join(', ')}.`,
          )
        }
        attachmentCount={imageAttachments.attachments.length}
        sendBlocked={imageAttachments.hasUnsentAttachments}
        attachments={
          <ImageAttachmentChips
            attachments={imageAttachments.attachments}
            onRemove={imageAttachments.removeAttachment}
            onRetry={imageAttachments.retryAttachment}
            refusal={imageAttachments.refusal}
            onDismissRefusal={imageAttachments.dismissRefusal}
          />
        }
        renderMessageImages={(message) => {
          const placeholders = message.metadata?.__image_placeholders;
          if (!Array.isArray(placeholders) || placeholders.length === 0) return null;
          return <MessageImagePlaceholders placeholders={placeholders as MessageImagePlaceholder[]} />;
        }}
        triggers={[
          ...(activeWorkspaceId ? [fileTrigger] : []),
          ...(skillTrigger ? [skillTrigger] : []),
          ...(hashTrigger ? [hashTrigger] : []),
        ]}
        onEditMessage={handleEditMessage}
        onRetryMessage={handleRetryMessage}
        isLoading={sdkChatState === 'starting' || sdkChatState === 'reasoning' || sessionEvents.isReasoning}
        allowSendWhileLoading={sessionEvents.executionState === 'running'}
        loadingContent={
          showAdvancedChrome ? (
            <ReasoningProgress
              isReasoning={sessionEvents.isReasoning}
              recentSteps={liveRun?.steps ?? []}
            />
          ) : undefined
        }
        belowMessagesSlot={
          liveRun ||
          (!sessionEvents.isReasoning &&
            sessionEvents.sessionContext?.parentContext) ? (
            <div className="space-y-1">
              {liveRun && showNormalStatus ? (
                <TurnStatusLine
                  rows={mapTurnStatus(liveRun, {
                    runPersonaName: selectedAgent?.name ?? 'Assistant',
                    liveCalls: liveLlmCalls,
                    interruptPending: Boolean(
                      sessionEvents.waitingOnUser ||
                        sessionEvents.pendingMediaActionApproval ||
                        sessionEvents.pendingPlatformApproval,
                    ),
                  })}
                />
              ) : null}
              {liveRun && showAdvancedChrome ? (
                <AgentEventTrail
                  key={`live-${liveRun.runId}`}
                  progressTree={liveRun}
                  internalAgentVisibility={internalAgentVisibility}
                  events={eventsByRun.get(liveRun.runId) ?? []}
                  getEventData={getEventData}
                  isReasoning={sessionEvents.isReasoning}
                  onRenderedWindowChange={(seqs) => reportTrailRenderedWindow('live', seqs)}
                />
              ) : null}
              {streamingAnswer ? <StreamingAnswer call={streamingAnswer} /> : null}
              {!sessionEvents.isReasoning && (
                <SessionContextSummaryChip
                  snapshot={sessionEvents.sessionContext?.parentContext ?? null}
                  contextFootprint={session?.context_footprint}
                  onOpenDetails={openContextDetails}
                />
              )}
            </div>
          ) : null
        }
        scrollSignal={
          (liveRun?.steps.length ?? 0) +
          (liveRun?.nodes.length ?? 0) +
          (streamingAnswer?.content.length ?? 0)
        }
        onMessageClick={handleMessageClick}
        onInputChange={handleInputChange}
        aboveInputSlot={
          <>
            {/* Optional workspace-attach on a draft — desktop only, draft only */}
            {isDraft && <WorkspaceAttachCallout />}
            {userQuestionCount > 0 && (
              <div className="mx-auto flex w-full max-w-2xl justify-end px-4 pb-2">
                <QuestionsIndicator count={userQuestionCount} onOpen={openQuestionsTab} />
              </div>
            )}
            {sessionEvents.pendingMediaActionApproval && (
              <div className="mx-auto w-full max-w-2xl px-4 pb-2">
                <MediaActionApprovalCard
                  request={sessionEvents.pendingMediaActionApproval}
                  onApprove={handleMediaActionApprove}
                  onDeny={handleMediaActionDeny}
                  isSubmitting={mediaApprovalBusy}
                  error={mediaApprovalError}
                  mode={mode}
                />
              </div>
            )}
            {boundChat && bindCurrent ? (
              <SubagentMediaApprovals chat={boundChat} participants={participants} mode={mode} />
            ) : null}
            {/* DESKTOP-only: a daemon-gated destructive platform_call. The
                `shogunchop.resource` gate keeps this off web (which uses the
                ShogunConfirmDialog modal instead). */}
            {shogunchop.resource &&
              sessionEvents.pendingPlatformApproval &&
              !dismissedApprovalKeys.has(sessionEvents.pendingPlatformApproval.idempotencyKey) && (
                <div className="mx-auto w-full max-w-2xl px-4 pb-2">
                  <ShogunApprovalCard
                    request={sessionEvents.pendingPlatformApproval}
                    onApprove={handleApprovePlatformCall}
                    onDismiss={handleDismissPlatformCall}
                    isApproving={
                      approvingKey === sessionEvents.pendingPlatformApproval.idempotencyKey
                    }
                    error={approvalError}
                    mode={mode}
                  />
                </div>
              )}
            <ExecutionControlBar
              sessionId={sessionId}
              executionState={sessionEvents.executionState}
              queue={queue}
              onStop={() => sessionEvents.stopExecution({ mode: 'turn' })}
              onStopAll={() => sessionEvents.stopExecution({ mode: 'all' })}
              backgroundRunning={primaryHasLiveWork}
              onRemoveQueueItem={async (id) => requireBound().removeQueueItem(id)}
              onClearQueue={async () => requireBound().clearQueue()}
              onMoveQueueItem={async (id, direction) => requireBound().moveQueueItem(id, direction)}
              onEditQueueItem={async (id, content) => requireBound().editQueueItem(id, content)}
              onRedirect={handleRedirect}
              inputHasText={inputHasText}
              waitingOnUser={sessionEvents.waitingOnUser}
              onOpenQuestions={openQuestionsTab}
            />
          </>
        }
        placeholder={
          sessionEvents.pendingMediaActionApproval
              ? 'Approve or deny the artifact action above...'
                : sessionEvents.executionState === 'running'
                  ? 'Type a message to queue...'
                  : 'Type a message... (Shift+Enter for new line)'
        }
        disabled={
          isDraft
            ? !boundChat || !selectedPersonaId || personaSettling
            : sessionEvents.connectionState !== 'connected'
        }
        renderMessageFooter={(msg) => {
          const diagrams = msg.role === 'assistant' ? <MessageDiagrams text={msg.content} /> : null;
          if (!ticketMapping?.identifier || msg.role !== 'assistant') return diagrams;
          const steps = msg.metadata?.planner_steps;
          if (!Array.isArray(steps)) return diagrams;
          const commentSteps = steps.filter(
            (s: Record<string, unknown>) =>
              s.type === 'tool_call' &&
              typeof s.tool_name === 'string' &&
              s.tool_name.includes('comment')
          );
          if (commentSteps.length === 0) return diagrams;
          return (
            <>
              {diagrams}
              <TicketCommentNotification
                identifier={ticketMapping.identifier}
                preview={msg.content.slice(0, 120)}
              />
            </>
          );
        }}
        headerSlot={includeHeader ? chatHeader : undefined}
      />
      </ChatImageScopeContext.Provider>
      </LiveLlmCallsProvider>
      </>
    );
  };

  const participantNodeById = useMemo(() => {
    const byId = new Map<string, ProgressNode>();
    for (const run of sessionEvents.reasoningRuns) {
      for (const node of run.nodes) byId.set(node.id, node);
    }
    return byId;
  }, [sessionEvents.reasoningRuns]);
  const participants = useChatParticipants(bindCurrent ? boundChat : null);
  const setFocusedActivityNode = useSetAtom(focusedActivityNodeAtom);
  const openRunningWork = useMemo(() => {
    if (!participantTabs) return null;
    return (item: BackgroundWorkItem) => {
      if (item.tabParticipantId === null) return;
      const primary = participants.find((p) => p.isPrimary);
      if (primary && item.tabParticipantId === primary.id) participantTabs.onSelect(null);
      else participantTabs.onOpen(item.tabParticipantId);
      if (item.kind === 'agent') return;
      if (showAdvancedChrome) setFocusedActivityNode({ nodeId: item.id, at: Date.now() });
      else if (item.replyMessageId) setScrollTargetMessageId(item.replyMessageId);
    };
  }, [participantTabs, participants, showAdvancedChrome, setFocusedActivityNode]);
  const primaryParticipantId = bindCurrent && boundChat ? boundChat.primaryParticipantId : null;
  const primaryHasLiveWork =
    primaryParticipantId !== null && boundChat !== null && boundChat.hasLiveWorkBelow(primaryParticipantId, { backgroundOnly: true });
  const chatTabs = useMemo(() => {
    if (!participantTabs) return [];
    return buildChatTabs(participants, participantTabs.openIds, (spawnTaskId) => {
      const spawn = participantNodeById.get(spawnTaskId);
      if (!spawn) return undefined;
      const cognitive = spawn.cognitiveToolType
        ? COGNITIVE_TOOL_LABELS[spawn.cognitiveToolType as CognitiveToolType]
        : undefined;
      return cognitive ?? spawn.label;
    });
  }, [participantTabs, participants, participantNodeById]);
  const selectedTab =
    participantTabs?.selectedId != null
      ? (chatTabs.find((t) => t.participantId === participantTabs.selectedId) ?? null)
      : null;

  const selectChatTab = (id: string | null) => {
    if (participantTabs) participantTabs.onSelect(id);
    else setLocalQuestionsTab(id === QUESTIONS_TAB_ID ? 'selected' : 'open');
  };
  const closeChatTab = (id: string) => {
    if (participantTabs) participantTabs.onClose(id);
    else if (id === QUESTIONS_TAB_ID) setLocalQuestionsTab('closed');
  };

  const renderTabbedContent = (includeHeader = true) => {
    if (chatTabs.length === 0 && !questionsTabOpen) return renderContent(includeHeader);
    return (
      <div className="flex h-full flex-col">
        <ChatTabs
          tabs={chatTabs}
          selectedId={questionsTabSelected ? QUESTIONS_TAB_ID : (selectedTab?.participantId ?? null)}
          onSelect={selectChatTab}
          onClose={closeChatTab}
          testIdPrefix={testIdPrefix}
          questionsTab={questionsTabOpen ? { count: userQuestionCount } : undefined}
        />
        <div className="min-h-0 flex-1">
          {questionsTabSelected ? (
            <div className="flex h-full flex-col">
              <div className="max-h-[50%] shrink-0 overflow-y-auto border-b border-border">
                <QuestionsTab
                  questions={sessionEvents.questions}
                  answered={sessionEvents.answeredQuestions}
                  onAnswer={sessionEvents.answerQuestion}
                />
              </div>
              <div className="min-h-0 flex-1">{renderContent(includeHeader)}</div>
            </div>
          ) : selectedTab && boundChat ? (
            <ParticipantTabView
              key={selectedTab.participantId}
              chat={boundChat}
              participantId={selectedTab.participantId}
              label={selectedTab.label}
              node={participantNodeById.get(selectedTab.participantId) ?? null}
              events={sessionEventSkeletons}
              getEventData={getEventData}
              disabled={sessionEvents.connectionState !== 'connected'}
              testIdPrefix={testIdPrefix}
              participants={participants}
              mode={mode}
            />
          ) : (
            renderContent(includeHeader)
          )}
        </div>
      </div>
    );
  };

  // Derive a single chat readiness state for test automation.
  // Tests wait for data-chat-state="ready" before interacting. A draft New Chat
  // is immediately ready (no session record to load).
  const chatState = isDraft
    ? !boundChat
      ? 'loading'
      : sdkChatState === 'starting'
        ? 'starting'
        : 'ready'
    : bindCurrent
      ? sdkChatState === 'starting'
        ? 'starting'
        : sessionEvents.isReasoning
          ? 'reasoning'
          : 'ready'
      : !workspaceResolved || loadingSession
        ? 'loading'
        : messagesError
          ? 'error'
          : !session
            ? 'no-session'
            : 'loading';

  useEffect(() => {
    onChatStateChange?.(chatState);
  }, [chatState, onChatStateChange]);

  // The chat column, wrapped in the split file browser (tree + tabbed viewer)
  // when a workspace is attached and files are shown; otherwise plain.
  const chatColumn =
    activeWorkspaceId && chatFilesOpen ? (
      <ChatWorkspaceLayout
        workspaceId={activeWorkspaceId}
        workspacePath={activeWorkspace?.binding?.folder_path}
        mode={activeWorkspace?.assignment.routing_class}
        onEntriesDiscovered={handleEntriesDiscovered}
        onNodeDragStart={handleNodeDragStart}
        fileBrowserRef={fileBrowserRef}
        header={chatHeader}
        chat={renderTabbedContent(false)}
      />
    ) : (
      <div className="flex-1 min-w-0">{renderTabbedContent()}</div>
    );

  if (missingNamedDefault) {
    return (
      <div
        className="flex h-full items-center justify-center p-4 text-center text-body text-destructive"
        data-testid={`${testIdPrefix}chat-missing-agent`}
      >
        The {defaultPersonaName} agent is not available on this account.
      </div>
    );
  }

  return (
    <OpenParticipantTabContext.Provider value={participantTabs ? participantTabs.onOpen : null}>
    <OpenRunningWorkContext.Provider value={openRunningWork}>
    <div
      className="flex h-full"
      data-ws-state={sessionEvents.connectionState}
      data-testid={`${testIdPrefix}chat-view`}
      data-session-id={sessionId ?? ''}
      data-experience-mode={mode}
      data-chat-state={chatState}
      data-message-count={messages?.length ?? 0}
    >
      {chatColumn}
      {dockOpen && (
        <div
          data-testid={`${testIdPrefix}chat-dock-resize`}
          onPointerDown={dockResize.onHandlePointerDown}
          title="Resize panel"
          aria-label="Resize panel"
          className="w-1 flex-shrink-0 cursor-col-resize touch-none select-none hover:bg-primary/30 active:bg-primary/50"
        />
      )}
      <div
        className="flex h-full flex-shrink-0 flex-col overflow-hidden border-l border-border bg-background"
        style={{ width: dockOpen ? dockResize.size : TASKS_COLUMN_WIDTH }}
        data-testid={`${testIdPrefix}chat-right-column`}
      >
        <TasksColumn
          sessionId={sessionId}
          chat={bindCurrent ? boundChat : null}
          testIdPrefix={testIdPrefix}
          progressLabel={sessionEvents.isReasoning ? 'running' : 'idle'}
          progress={dockOpen ? (
        <>
          <div
            className="flex min-h-0 flex-1 flex-col overflow-hidden"
            data-testid={`${testIdPrefix}chat-dock`}
          >
            {/* Dock tab bar — Progress / Events (files live in their own panel) */}
            <div className="flex items-center justify-between border-b border-border px-3 py-2">
              <div className="flex overflow-hidden rounded-md bg-muted text-micro font-medium">
                {dockTabs.map((t) => (
                  <button
                    key={t}
                    data-testid={`${testIdPrefix}debug-tab-${t}`}
                    aria-selected={effectiveDockTab === t}
                    role="tab"
                    className={`px-2.5 py-1 capitalize transition-colors ${effectiveDockTab === t ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                    onClick={() => setDockTab(t)}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1">
                {session && !isPoppedOut && (
                  <button
                    onClick={handlePopOut}
                    className="rounded-md p-1 transition-colors hover:bg-accent"
                    title="Pop out to separate window"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h6" />
                      <path d="m21 3-9 9" />
                      <path d="M15 3h6v6" />
                    </svg>
                  </button>
                )}
                <button
                  onClick={() => setChatDockOpen(false)}
                  className="rounded-md p-1 transition-colors hover:bg-accent"
                  title="Hide side panel"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
              </div>
            </div>
            {/* Active tab body */}
            <div className="min-h-0 flex-1 overflow-hidden">
              {session && isPoppedOut ? (
                <div className="flex h-full items-center justify-center p-4 text-center text-body text-muted-foreground">
                  Panel popped out.
                  <button className="ml-1 underline" onClick={handlePopIn}>
                    Pop back in
                  </button>
                </div>
              ) : session ? (
                <ChatDebugPanel
                  session={session}
                  showChrome={false}
                  mode={effectiveDockTab === 'events' ? 'events' : 'progress'}
                  reasoningRuns={sessionEvents.reasoningRuns}
                  isReasoning={sessionEvents.isReasoning}
                  selectedRunId={selectedRunId}
                  onSelectRun={handleSelectRun}
                  hasOlderRuns={hasOlderRuns}
                  loadingOlderRuns={loadingOlderRuns}
                  olderRunsError={olderRunsError}
                  hasNewerRuns={hasNewerRuns}
                  loadingNewerRuns={loadingNewerRuns}
                  newerRunsError={newerRunsError}
                  onLoadNewerRuns={loadNewerRuns}
                  runsGap={runsGap}
                  onVisibleRunsChange={reportVisibleRuns}
                  onLoadOlderRuns={loadOlderRuns}
                  sessionId={sessionId}
                  sessionContext={sessionEvents.sessionContext}
                  pinChangeCounter={sessionEvents.pinChangeCounter}
                  events={sessionEvents.events}
                  sessionContextChars={sessionContextChars}
                  getRagHits={getRagHits}
                />
              ) : null}
            </div>
          </div>
        </>
          ) : null}
        />
      </div>
      {showAdvancedChrome &&
        session &&
        isPoppedOut &&
        popoutContainer &&
        createPortal(
          <div className="h-screen">
            <ChatDebugPanel
              session={session}
              onClose={handlePopIn}
              mode={effectiveDockTab === 'events' ? 'events' : 'progress'}
              onModeChange={(m) => setDockTab(m)}
              reasoningRuns={sessionEvents.reasoningRuns}
              isReasoning={sessionEvents.isReasoning}
              selectedRunId={selectedRunId}
              onSelectRun={handleSelectRun}
              hasOlderRuns={hasOlderRuns}
              loadingOlderRuns={loadingOlderRuns}
              olderRunsError={olderRunsError}
              hasNewerRuns={hasNewerRuns}
              loadingNewerRuns={loadingNewerRuns}
              newerRunsError={newerRunsError}
              onLoadNewerRuns={loadNewerRuns}
              runsGap={runsGap}
              onVisibleRunsChange={reportVisibleRuns}
              onLoadOlderRuns={loadOlderRuns}
              sessionId={sessionId}
              sessionContext={sessionEvents.sessionContext}
              pinChangeCounter={sessionEvents.pinChangeCounter}
              events={sessionEvents.events}
              sessionContextChars={sessionContextChars}
              getRagHits={getRagHits}
            />
          </div>,
          popoutContainer
        )}
    </div>
    </OpenRunningWorkContext.Provider>
    </OpenParticipantTabContext.Provider>
  );
}
